import { Component, ViewContainerRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormArray, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { PhotoService } from '../_services/photo.service';
import { Question, TriviaService, TriviaGameFormData, TriviaGameListView, TriviaServiceFileType, TriviaServiceFileCollection } from '../_services/trivia-service';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { PaymentService } from '../_services/payment-service.service';
import { DialogService } from '../_services/dialog.service';
import { UserService } from '../_services/user.service';
import * as moment from 'moment';
import { BaseCueServiceComponent as CueServiceComponentBase } from '../_shared/base.cueservice.component';
import { map } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { LightShowTrackService } from '../_services/track.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';

@Component({
  selector: 'app-trivia',
  templateUrl: './trivia.component.html',
  styleUrls: ['./trivia.component.css']
})
export class TriviaComponent extends CueServiceComponentBase<TriviaGameFormData, TriviaGameListView> {

  public loadingPngFile: boolean;
  public selectedQuestionIndex = 0;
  public emails: string[] = [];
  public showServicePeriod: boolean;
  private searchTerm = '';
  private showDuplicateErrorMessage = false;
  private validSubscription = false;
  public redirectType = -1;
  public serviceFiles = new TriviaServiceFileCollection();

  private mp3File: File;
  private mp3FileUrl: string;

  private _hasSiteManagement = false;
  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  private DEFAULT_TRIVIA_RULES = {
    rule1: 'Answer quickly for more points. Do not background the app.',
    rule2: 'Your flashlight will stay lit if you answered correctly.',
    rule3: 'Players with the most points wins!',
    startsInLabelText: 'Game starts in:'
  }

  constructor(
    private fb: FormBuilder,
    triviaService: TriviaService,
    photoService: PhotoService,
    router: Router,
    route: ActivatedRoute,
    viewRef: ViewContainerRef,
    spinner: NgxSpinnerService,
    paymentService: PaymentService,
    dialogService: DialogService,
    userService: UserService,
    private sanitizer: DomSanitizer,
    private trackService: LightShowTrackService,
    dbService: NgxIndexedDBService
  ) {
    super(triviaService, photoService, router, route, spinner, paymentService, dialogService, viewRef, userService, dbService);
  }

  protected getInitialDataForNewItem(): Observable<TriviaGameFormData> {
    return of(<TriviaGameFormData>{
      name: null,
      couponId: null,
      id: null,
      imageUrl: null,
      gameTitle: 'New game',
      startDate: new Date().toString(),
      endDate: moment()
        .add(30, 'days')
        .toDate()
        .toString(),
      winnerText: '',
      loserText: '',
      numberOfWinners: 5,
      rulesDisplayDuration: 45,
      timeToAnswer: 10,
      questions: [this.createDefaultQuestion()],
      navBarColor: '#323748',
      selectedAnswerColor: '#323748',
      listOfEmails: [],
      winnerCouponId: null,
      loserCouponId: null,
      winnerUrl: null,
      loserUrl: null,
      regenerateAudio: false,
      insertionPattern: '',
      useAudible: false,
      embedPosition: 0,
      embedMusic: false,
      embedMp3Url: null,
      showRules: false,
      title: 'Trivia',
      rule1: this.DEFAULT_TRIVIA_RULES.rule1,
      rule2: this.DEFAULT_TRIVIA_RULES.rule2,
      rule3: this.DEFAULT_TRIVIA_RULES.rule3,
      startsInLabelText: this.DEFAULT_TRIVIA_RULES.startsInLabelText,
      dateCreated: null,
      serviceFiles: null
    });
  }

  protected loadLookupData(): Observable<any> {
    const siteManagementObs = this.userService.hasSinglePermission('SiteManagement').pipe(map(sm => this._hasSiteManagement = sm));

    return forkJoin([siteManagementObs]);
  }
   validateUrl (fieldName: string) {
    const regex = new RegExp(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/gm);

    const controlValue = this.form.controls[fieldName].value;

    if (regex.test(controlValue) && controlValue.length > 0) {
      return
    }

    this.form.controls[fieldName].setErrors({invalid: true})
    this.form.controls[fieldName].markAsTouched();
  }

  public getFormData(form: FormGroup, includeServiceFiles: boolean = false): TriviaGameFormData {
    const data = {
      name: null,
      couponId: null,
      gameTitle: form.get('gameTitle').value,
      startDate: this.formatDate(form.get('period').value.startDate),
      endDate: this.formatDate(form.get('period').value.endDate),
      imageUrl: form.get('imageUrl').value,
      questions: form.get('questions').value,
      timeToAnswer: form.get('timeToAnswer').value,
      rulesDisplayDuration: form.get('segments.0.duration').value,
      numberOfWinners: form.get('numberOfWinners').value,
      winnerText: form.get('winnerText').value,
      loserText: form.get('loserText').value,
      navBarColor: form.get('navBarColor').value,
      selectedAnswerColor: form.get('selectedAnswerColor').value,
      listOfEmails: form.get('listOfEmails').value.map(item => item.label),
      winnerCouponId: form.get('winnerCouponId').value,
      loserCouponId: form.get('loserCouponId').value,
      winnerUrl: form.get('winnerUrl').value,
      loserUrl: form.get('loserUrl').value,
      regenerateAudio: form.get('regenerateAudio').value,
      insertionPattern: form.get('insertionPattern').value,
      useAudible: form.get('useAudible').value,
      embedMp3Url: form.get('embedMp3Url').value,
      embedMusic: form.get('embedMusic').value,
      embedPosition: form.get('segments.0.position').value,
      showRules: form.get('showRules').value,
      title: form.get('title').value,
      rule1: form.get('rule1').value,
      rule2: form.get('rule2').value,
      rule3: form.get('rule3').value,
      startsInLabelText: form.get('startsInLabelText').value,
      dateCreated: new Date(Date.now()),
      serviceFiles: includeServiceFiles ? this.serviceFiles : null
    };

    if (!data.embedMusic) {
      data.embedMp3Url = null;
    }

    return data;
  }

  protected setFormData(data: TriviaGameFormData, userSubscription: any, savedData?: TriviaGameFormData): FormGroup {
    if (data.questions.length === 0) {
      data.questions.push(this.createDefaultQuestion());
    }

    localStorage.removeItem('CountDownMessage');

    const form = this.fb.group({
      id: this.id,
      gameTitle: [data.gameTitle, Validators.required],
      period: { startDate: new Date(data.startDate), endDate: new Date(data.endDate) },
      imageUrl: [data.imageUrl, Validators.required],
      questions: this.getQuestionsFormArray(data.questions),
      timeToAnswer: [data.timeToAnswer, [Validators.required, Validators.pattern('[0-9]+'), Validators.min(1), Validators.max(60)]],
      numberOfWinners: [data.numberOfWinners, [Validators.required, Validators.pattern('[0-9]+'), Validators.min(0), Validators.max(10000)]],
      winnerText: [data.winnerText, [Validators.required, Validators.minLength(1), Validators.maxLength(150)]],
      loserText: [data.loserText, [Validators.required, Validators.minLength(1), Validators.maxLength(150)]],
      navBarColor: [data.navBarColor, [Validators.required, Validators.pattern('#([A-Fa-f0-9]{6})')]],
      selectedAnswerColor: [data.selectedAnswerColor, [Validators.required, Validators.pattern('#([A-Fa-f0-9]{6})')]],
      regenerateAudio: [false],
      listOfEmails: [data.listOfEmails.map(e => ({label: e})), [
        ((): ValidatorFn => {
          return (control: AbstractControl): { [key: string]: any } | null => {
            let isValid = true;
            if (control.value !== null) {
              control.value.forEach(em => {
                if (!this.isEmailValid(em.email || em.label)) {
                  isValid = false;
                }
              });
            }
            return isValid
              ? null
              : { 'email': { value: control.value } };
          };
        })()
        ]
      ],
      winnerUrl: [data.winnerUrl],
      loserUrl: [data.loserUrl],
      winnerCouponId: [data.winnerCouponId],
      loserCouponId: [data.loserCouponId],
      insertionPattern: [data.insertionPattern, Validators.pattern(/^\s*((-\d)+|(\d)+)\s*(\s*,\s*((-\d)+|(\d)+))*\s*$/)],
      useAudible: [data.useAudible],
      embedMusic: [data.embedMusic],
      segments: this.fb.array([
        this.fb.group({
          color: 'lightskyblue',
          position: [data.embedPosition],
          duration: [data.rulesDisplayDuration, [Validators.required, Validators.pattern('[0-9]+'), this.validateDuration]]
        })
      ]),
      embedMp3Url: [data?.embedMp3Url || ''],
      showRules: [data?.showRules || false],
      title: [data?.title || 'Trivia', [Validators.required, Validators.maxLength(20)]],
      rule1: [data?.rule1 || this.DEFAULT_TRIVIA_RULES.rule1, [Validators.required, Validators.maxLength(65)]],
      rule2: [data?.rule2 || this.DEFAULT_TRIVIA_RULES.rule2, [Validators.required, Validators.maxLength(65)]],
      rule3: [data?.rule3 || this.DEFAULT_TRIVIA_RULES.rule3, [Validators.required, Validators.maxLength(65)]],
      startsInLabelText: [data?.startsInLabelText || this.DEFAULT_TRIVIA_RULES.startsInLabelText, [Validators.required, Validators.maxLength(20)]]
    });

    if (data.winnerCouponId > 0 || data.loserCouponId > 0 ) {
      this.redirectType = 1;
    } else if ((data.winnerUrl && data.winnerUrl !== '') || (data.loserUrl && data.loserUrl !== '')) {
      this.redirectType = 0;
    }

    if (userSubscription.subscriptionStartDate && userSubscription.subscriptionEndDate) {
      form.controls.period.patchValue({
        startDate: new Date(userSubscription.subscriptionStartDate),
        endDate: new Date(userSubscription.subscriptionEndDate)
      });
      form.controls.period.disable();
      this.showServicePeriod = false;
    } else {
      this.showServicePeriod = true;
    }

    return form;
  }

  uploadPng(file: File) {
    this.loadingPngFile = true;

    if (file && file.type === 'image/png') {

      this.photoService.meetsImageConstraints(file, 300, 300)
        .subscribe(
          res => {

            if (res) {

              this.serviceFiles.addBrandingImageFile(file);

              this.photoService.enqueueUploadPhoto(file, 'trivia image', { height: 300, width: 300 }, false, true).subscribe(res => {
                this.form.patchValue({ imageUrl: res.url });
                this.form.controls.imageUrl.setErrors(null);
                this.loadingPngFile = false;
              });

            } else {
              this.form.controls.imageUrl.setErrors({ incorrectSize: true });
              this.form.controls.imageUrl.markAsTouched();
              this.loadingPngFile = false;
            }
          }
        );

    } else {
      this.form.controls.imageUrl.setErrors({'Not PNG': true});
      this.loadingPngFile = false;
    }

    this.form.markAsDirty();
  }

  protected uploadContent(): Observable<any> {
    return this.mp3File
      ? this.trackService.uploadTrack(this.mp3File, false).pipe(map(res => {
          URL.revokeObjectURL(this.mp3FileUrl);
          this.form.patchValue({ embedMp3Url: res.trackUrl });
        }))
      : of(null);
  }

  redirectChange(event) {
    if (+this.redirectType === 0 || +this.redirectType === 1) {
      this.form.controls.loserCouponId.setValidators(null);
      this.form.controls.winnerCouponId.setValidators(null);

      this.form.controls.winnerCouponId.patchValue(0);
      this.form.controls.winnerCouponId.updateValueAndValidity();
      this.form.controls.winnerCouponId.setErrors(null);

      this.form.controls.loserCouponId.patchValue(0);
      this.form.controls.loserCouponId.updateValueAndValidity();
      this.form.controls.loserCouponId.setErrors(null);

      this.form.controls.winnerUrl.setValidators([Validators.required]);
      this.form.controls.loserUrl.setValidators([Validators.required]);
    } else

    if (+this.redirectType === 1 || +this.redirectType === -1) {
      this.form.controls.winnerUrl.setValidators(null);
      this.form.controls.loserUrl.setValidators(null);

      this.form.controls.winnerUrl.patchValue(null);
      this.form.controls.winnerUrl.updateValueAndValidity();
      this.form.controls.winnerUrl.setErrors(null);

      this.form.controls.loserUrl.patchValue(null);
      this.form.controls.loserUrl.updateValueAndValidity();
      this.form.controls.loserUrl.setErrors(null);

      this.form.controls.loserCouponId.setValidators([Validators.required]);
      this.form.controls.winnerCouponId.setValidators([Validators.required]);
    }
  }

  private validateDuration(c: AbstractControl): ValidationErrors {
    if (c.parent) {
      const form = c.root as FormGroup;
      return Validators.min(15)(c) || Validators.max(600)(c);
    }
    return null;
  }

  timerChange() {
    const m = localStorage.getItem('CountDownMessage');
    if (this.id > 0 && !m) {
      this.dialogService.alert(
        'Please note!',
        `Changing the Countdown Timer will require you to redownload your trivia game audio file`,
        this.viewRef);
      }
      localStorage.setItem('CountDownMessage', 'true');
  }

  removeImage() {

    this.serviceFiles.removeBrandingImageFile();

    this.form.patchValue({ imageUrl: null });
    this.form.markAsDirty();
    this.loadingPngFile = false;
  }

  viewResults(serviceId: number, triviaId: number) {
    this.router.navigate(['/portal/trivia/results/', serviceId, triviaId]);
  }

  nextQuestion() {
    this.selectedQuestionIndex = (this.selectedQuestionIndex + 1) % this.form.controls.questions.value.length;
  }

  prevQuestion() {
    this.selectedQuestionIndex = (this.selectedQuestionIndex + this.form.controls.questions.value.length - 1) % this.form.controls.questions.value.length;
  }

  newQuestion() {
    (<FormArray>this.form.controls.questions).push(this.fb.group(this.createDefaultQuestion()));
    this.form.controls.questions.markAsDirty();
    this.selectedQuestionIndex = this.form.controls.questions.value.length - 1;
  }

  deleteQuestion() {
    (<FormArray>this.form.controls.questions).removeAt(this.selectedQuestionIndex);
    this.form.controls.questions.markAsDirty();
    this.selectedQuestionIndex = Math.min(this.selectedQuestionIndex, (<FormArray>this.form.controls.questions).length - 1);
  }

  onEmbedMusicChanged(){
    this.form.controls.embedMp3Url.clearValidators();
    this.form.controls.embedMp3Url.updateValueAndValidity();

    if (this.form.controls.embedMusic.value) {
      this.form.markAsDirty();
      this.form.controls.embedMp3Url.setValidators([Validators.required]);
      this.form.controls.embedMp3Url.updateValueAndValidity();
    }
  }

  public uploadMp3(files: File[]) {
    if (files.length > 0) {
      this.mp3File = files[0];

      this.serviceFiles.addAudio(this.mp3File);

      this.mp3FileUrl = URL.createObjectURL(this.mp3File);
      this.form.patchValue({ embedMp3Url: <any>this.sanitizer.bypassSecurityTrustUrl(this.mp3FileUrl) });
      this.form.get('embedMp3Url').markAsDirty();
    }
  }

  protected loadServiceFiles(data: TriviaGameFormData) {
    
    for (let i = 0; i < data.serviceFiles?.files.length; i++) {

      switch(data.serviceFiles.files[i].fileType) {

        case TriviaServiceFileType.Image:
          this.uploadPng(data.serviceFiles.files[i].file);
          break;
        
        case TriviaServiceFileType.Audio:
          this.uploadMp3([data.serviceFiles.files[i].file]);
          break;

        default: 
          break;
      }
    }
  }

  private createDefaultQuestion(): Question {
    return <Question>{
      question: '',
      answerOne: '',
      answerTwo: '',
      answerThree: '',
      answerFour: '',
      correctAnswer: 0
    };
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  private getQuestionsFormArray(questions: Question[]) {

    const qFormArray = this.fb.array(questions.map(q => this.fb.group(q)));
    qFormArray.controls[0].controls.question.setValidators(Validators.required)

    return qFormArray;
  }


  public cancel() {
    this.navigateUp();
  }
}

